// Spreadsheets inside a plain text note: a fenced ```sheet block holding a
// pipe table, markdown style. A cell starting with `=` is a formula:
//
//   ```sheet
//   | item | cost        |
//   |------|-------------|
//   | rent | 1200        |
//   | food | 400         |
//   | sum  | =SUM(B2:B3) |
//   ```
//
// Rows are numbered from 1 counting the header and skipping the `---` rule,
// so B2 above is 1200. Formulas take numbers, cell references, ranges, the
// four operators and SUM/AVG/MIN/MAX/COUNT/ROUND, and these, which take
// text too:
//
//   =SUMIF(A2:A9, "food", B2:B9)   B where A is food (any case, * and ?)
//   =SUMIF(B2:B9, ">100")          B where B is over 100
//   =COUNTIF(A2:A9, "f*")          rows where A starts with f
//   =COUNTIFS(A2:A9, "<>food", A2:A9, "<>")
//                                  rows where A is neither food nor empty;
//                                  any number of pairs, ranges the same shape
//   =COUNTA(A2:A99)                cells in A that aren't empty
//   =SECONDS(A2)                   the m:ss or h:mm:ss in A2 as seconds
//
// DURATION gives text, so it stands only as the whole formula:
//
//   =DURATION(B2*60)               seconds as m:ss, or h:mm:ss from an hour
//
// COUNTBY spills: from its own cell it fills a block down and to the right,
// each distinct value in the range (text in any case counts as one, first
// seen spelling kept) next to how many times it comes up, in the order they
// first do. The block covers empty cells only, #SPILL! if any are taken.
//
//   =COUNTBY(A2:A99)

import {
  type Fenced,
  findFenced,
} from '@/app/fence';

// `spilled` marks an empty cell showing a value from a COUNTBY block, and
// names the cell whose formula it comes from
export type Cell = {
  raw: string,
  value: string | number,
  error?: string,
  spilled?: { row: number, col: number },
};

type Grid = (number | string)[][];

// A block's place in the note, and its table
export interface Sheet extends Fenced {
  table: string[][];
}

const RULE = /^\|?\s*:?-{3,}/;

export const findSheets = (body: string): Sheet[] =>
  findFenced(body, 'sheet').map(block => ({
    ...block,
    table: parseTable(block.content),
  }));

export const parseTable = (text: string): string[][] =>
  text.split('\n')
    .map(line => line.trim())
    .filter(line => line && !RULE.test(line))
    .map(line =>
      line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
    );

// Back to a pipe table, padded so the text stays readable, with the rule
// under the first row. Rows are made as wide as the widest; `|` inside a cell
// would split it, so it is dropped.
export const serializeTable = (table: string[][]): string => {
  const width = Math.max(1, ...table.map(r => r.length));
  const rows = table.map(r =>
    Array.from({ length: width }, (_, c) => (r[c] ?? '').replace(/\|/g, ''))
  );
  const sizes = Array.from(
    { length: width },
    (_, c) => Math.max(3, ...rows.map(r => r[c].length)),
  );
  const line = (cells: string[]) =>
    `| ${cells.map((cell, c) => cell.padEnd(sizes[c])).join(' | ')} |`;
  const rule = `|${sizes.map(n => '-'.repeat(n + 2)).join('|')}|`;
  return rows.map((r, i) => (i === 0 ? `${line(r)}\n${rule}` : line(r)))
    .map(l => `${l}\n`).join('');
};

// 0 -> A, 25 -> Z, 26 -> AA
export const columnName = (index: number): string =>
  (index >= 26 ? columnName(Math.floor(index / 26) - 1) : '')
  + String.fromCharCode(65 + (index % 26));

class FormulaError extends Error {}

type Value = number | number[];

const columnIndex = (letters: string) =>
  [...letters.toUpperCase()].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0)
  - 1;

// Where index `i` lands when a row or column goes in (`by` 1) or comes out
// (`by` -1) at index `at`, null if it was the one taken out. A range's ends
// move apart from each other: `end` is 'lo' or 'hi' for which end `i` is,
// so a range over a deleted line shrinks around it rather than breaking.
const shiftIndex = (
  i: number,
  at: number,
  by: 1 | -1,
  end?: 'lo' | 'hi',
): number | null => {
  if (by === 1) return i >= at ? i + 1 : i;
  if (i !== at) return i > at ? i - 1 : i;
  if (end === 'lo') return i; // the line below moves up into its place
  if (end === 'hi') return i - 1;
  return null;
};

const REF_OR_TEXT = /"[^"]*"|\b([A-Za-z]+)(\d+)(?::([A-Za-z]+)(\d+))?\b/g;

type Pos = { row: number, col: number };

// a block of cells, its corners included
export type Range = {
  top: number,
  left: number,
  bottom: number,
  right: number,
};

// Each reference in the formula - a cell, or a range's two ends - through
// `move`, which gives the new ends, or null for #REF!. Text in quotes is
// left alone.
const mapRefs = (
  formula: string,
  move: (ends: Pos[]) => Pos[] | null,
): string =>
  formula.replace(
    REF_OR_TEXT,
    (
      match,
      fromCol?: string,
      fromRow?: string,
      toCol?: string,
      toRow?: string,
    ) => {
      if (!fromCol || !fromRow) return match;
      const ends = [
        [fromCol, fromRow],
        ...(toCol && toRow ? [[toCol, toRow]] : []),
      ]
        .map(([letters, digits]) => ({
          row: Number(digits) - 1,
          col: columnIndex(letters),
        }));
      const moved = move(ends);
      return moved
        ? moved.map(e => `${columnName(e.col)}${e.row + 1}`).join(':')
        : '#REF!';
    },
  );

// The formula with its references moved for a row or column inserted (`by`
// 1) or deleted (`by` -1) at index `at`, as spreadsheets do: what lies past
// it shifts, a range reaching over it grows or shrinks, and a reference to a
// deleted cell, or a range left with nothing, becomes #REF!.
export const shiftRefs = (
  formula: string,
  axis: 'row' | 'col',
  at: number,
  by: 1 | -1,
): string =>
  mapRefs(formula, ends => {
    const along = ends.map(e => e[axis]);
    const lo = Math.min(...along);
    const hi = Math.max(...along);
    // a range one line thick along the axis is gone with that line
    const moved = along.map(i =>
      ends.length === 1 || lo === hi
        ? shiftIndex(i, at, by)
        : shiftIndex(i, at, by, i === lo ? 'lo' : 'hi')
    );
    if (moved.some(i => i === null)) return null;
    return ends.map((e, k) => ({ ...e, [axis]: moved[k]! }));
  });

// The formula with its references into the block `from` moved `rows` down
// and `cols` right, following cells moved or copied together. A range goes
// only when all of it lies in the block.
export const moveRefs = (
  formula: string,
  from: Range,
  rows: number,
  cols: number,
): string =>
  mapRefs(
    formula,
    ends =>
      ends.every(e =>
          e.row >= from.top && e.row <= from.bottom
          && e.col >= from.left && e.col <= from.right
        )
        ? ends.map(e => ({ row: e.row + rows, col: e.col + cols }))
        : ends,
  );

const FUNCTIONS: Record<string, (args: Value[]) => number> = {
  SUM: args => args.flat().reduce((a, b) => a + b, 0),
  AVG: args => {
    const all = args.flat();
    if (!all.length) throw new FormulaError('#DIV/0!');
    return all.reduce((a, b) => a + b, 0) / all.length;
  },
  MIN: args => Math.min(...args.flat()),
  MAX: args => Math.max(...args.flat()),
  COUNT: args => args.flat().length,
  ROUND: ([x, digits = 0]) => {
    const f = 10 ** scalar(digits);
    return Math.round(scalar(x) * f) / f;
  },
};
FUNCTIONS.AVERAGE = FUNCTIONS.AVG;

const COMPARE: Record<string, (a: number, b: number) => boolean> = {
  '=': (a, b) => a === b,
  '<>': (a, b) => a !== b,
  '<': (a, b) => a < b,
  '>': (a, b) => a > b,
  '<=': (a, b) => a <= b,
  '>=': (a, b) => a >= b,
};

// A SUMIF criterion, as spreadsheets take it: a number matches that number;
// text may lead with = <> < > <= >=, then holds a number, compared as one, or
// text, compared ignoring case, with * and ? as wildcards. "" is empty.
const matcher = (
  criterion: number | string,
): (v: number | string) => boolean => {
  if (typeof criterion === 'number') return v => v === criterion;
  const [, op = '=', operand] = criterion.match(/^(<=|>=|<>|<|>|=)?(.*)$/s)!;
  const n = asNumber(operand.trim());
  if (n !== null) {
    return v => typeof v === 'number' ? COMPARE[op](v, n) : op === '<>';
  }
  if (op === '=' || op === '<>') {
    const pattern = new RegExp(
      `^${
        operand.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')
          .replace(/\?/g, '.')
      }$`,
      'i',
    );
    const hit = (v: number | string) =>
      typeof v === 'string' && pattern.test(v);
    return op === '=' ? hit : v => !hit(v);
  }
  return v =>
    typeof v === 'string'
    && COMPARE[op](
      v.localeCompare(operand, undefined, { sensitivity: 'base' }),
      0,
    );
};

const scalar = (v: Value): number => {
  if (Array.isArray(v)) throw new FormulaError('#VALUE!');
  return v;
};

const TOKEN =
  /\s*(?:"([^"]*)"|(\d+(?:\.\d+)?)|([A-Za-z]+\d+(?::[A-Za-z]+\d+)?)|([A-Za-z]+)(?=\s*\()|([-+*/(),]))/y;

type Token =
  | { kind: 'num', value: number }
  | { kind: 'str', value: string }
  | { kind: 'ref', value: string }
  | { kind: 'fn', value: string }
  | { kind: 'op', value: string };

const tokenize = (src: string): Token[] => {
  const tokens: Token[] = [];
  TOKEN.lastIndex = 0;
  while (TOKEN.lastIndex < src.length) {
    if (/^\s*$/.test(src.slice(TOKEN.lastIndex))) break;
    const m = TOKEN.exec(src);
    if (!m) {
      const rest = src.slice(TOKEN.lastIndex).trimStart();
      throw new FormulaError(rest.startsWith('#REF!') ? '#REF!' : '#PARSE!');
    }
    if (m[1] !== undefined) tokens.push({ kind: 'str', value: m[1] });
    else if (m[2]) tokens.push({ kind: 'num', value: Number(m[2]) });
    else if (m[3]) tokens.push({ kind: 'ref', value: m[3].toUpperCase() });
    else if (m[4]) tokens.push({ kind: 'fn', value: m[4].toUpperCase() });
    else tokens.push({ kind: 'op', value: m[5] });
  }
  return tokens;
};

// a cell's number, or its text; empty is ''
type Lookup = (row: number, col: number) => number | string;

// Recursive descent over expr := term (('+'|'-') term)*, and so on down.
const parseFormula = (
  src: string,
  lookup: Lookup,
): number | string | Grid => {
  const tokens = tokenize(src);
  let i = 0;
  const peek = () => tokens[i];
  const take = (value?: string) => {
    const t = tokens[i];
    if (!t || (value !== undefined && t.value !== value)) {
      throw new FormulaError('#PARSE!');
    }
    i++;
    return t;
  };

  const cell = (ref: string) => {
    const [, letters, digits] = ref.match(/^([A-Z]+)(\d+)$/)!;
    return { row: Number(digits) - 1, col: columnIndex(letters) };
  };

  // the cells a ref covers, row by row, as offsets from its top left
  const span = (value: string) => {
    const [from, to = from] = value.split(':');
    const a = cell(from), b = cell(to);
    const top = Math.min(a.row, b.row), left = Math.min(a.col, b.col);
    const cols = Math.abs(a.col - b.col) + 1;
    const offsets = Array.from(
      { length: (Math.abs(a.row - b.row) + 1) * cols },
      (_, k) => ({ row: Math.floor(k / cols), col: k % cols }),
    );
    return { top, left, offsets };
  };

  const numberOrZero = (v: number | string) => typeof v === 'number' ? v : 0;

  const ref = (value: string): Value => {
    if (!value.includes(':')) {
      const { row, col } = cell(value);
      return numberOrZero(lookup(row, col));
    }
    const { top, left, offsets } = span(value);
    // text and empty cells are skipped, as spreadsheets do in ranges
    return offsets.map(o => lookup(top + o.row, left + o.col))
      .filter((v): v is number => typeof v === 'number');
  };

  const rangeArg = () => {
    const t = take();
    if (t.kind !== 'ref') throw new FormulaError('#VALUE!');
    return span(t.value);
  };

  // a ref to one cell standing as a whole argument, taken if there is one
  const loneCell = () => {
    const t = peek();
    const next = tokens[i + 1]?.value;
    if (
      t?.kind !== 'ref' || t.value.includes(':')
      || (next !== ',' && next !== ')')
    ) return undefined;
    i++;
    return cell(t.value);
  };

  // a range standing as a whole argument, taken if there is one
  const loneRange = () => {
    const t = peek();
    const next = tokens[i + 1]?.value;
    if (t?.kind !== 'ref' || (next !== ',' && next !== ')')) return undefined;
    i++;
    return span(t.value);
  };

  // text in quotes, a lone cell as it stands, text or not, or a number
  const criterionArg = (): number | string => {
    const t = peek();
    if (t?.kind === 'str') return (i++, t.value);
    const lone = loneCell();
    return lone ? lookup(lone.row, lone.col) : scalar(expr());
  };

  // `(a, b, ...)`, each argument read by `arg`
  const argList = <T>(arg: () => T): T[] => {
    take('(');
    const args: T[] = [];
    if (peek()?.value !== ')') {
      args.push(arg());
      while (peek()?.value === ',') {
        take(',');
        args.push(arg());
      }
    }
    take(')');
    return args;
  };

  // a range and the criterion its cells are tested against
  const criterionPair = () => {
    const range = rangeArg();
    take(',');
    return { range, test: matcher(criterionArg()) };
  };

  // the offsets, from each range's top left, whose cells all pass their
  // criteria; the ranges are laid over one another, so they match in shape
  const passing = (pairs: ReturnType<typeof criterionPair>[]) => {
    const [first] = pairs;
    const corner = ({ offsets }: { offsets: Pos[] }) =>
      offsets[offsets.length - 1];
    const shape = corner(first.range);
    if (
      pairs.some(({ range }) =>
        corner(range).row !== shape.row || corner(range).col !== shape.col
      )
    ) throw new FormulaError('#VALUE!');
    return first.range.offsets.filter(o =>
      pairs.every(({ range, test }) =>
        test(lookup(range.top + o.row, range.left + o.col))
      )
    );
  };

  // A range and a criterion, the first two arguments of SUMIF and COUNTIF:
  // the range, and its offsets whose cells pass the criterion.
  const matching = () => {
    const pair = criterionPair();
    return { range: pair.range, hits: passing([pair]) };
  };

  // These read their ranges cell by cell, text included, so they are parsed
  // here rather than living in FUNCTIONS.
  const SPECIAL: Record<string, () => number> = {
    // SUMIF(range, criterion[, sum range]): the sum range is laid over the
    // range from its top left, as spreadsheets do.
    SUMIF: () => {
      take('(');
      const { range, hits } = matching();
      const target = peek()?.value === ',' ? (take(','), rangeArg()) : range;
      take(')');
      return hits.reduce(
        (sum, o) =>
          sum + numberOrZero(lookup(target.top + o.row, target.left + o.col)),
        0,
      );
    },
    COUNTIF: () => {
      take('(');
      const { hits } = matching();
      take(')');
      return hits.length;
    },
    // COUNTIFS(range, criterion, range, criterion, ...)
    COUNTIFS: () => {
      take('(');
      const pairs = [criterionPair()];
      while (peek()?.value === ',') {
        take(',');
        pairs.push(criterionPair());
      }
      take(')');
      return passing(pairs).length;
    },
    // what isn't empty: text and numbers in ranges and cells, and each value
    // it fills a block, so it stands only as the whole formula
    COUNTBY: () => {
      throw new FormulaError('#VALUE!');
    },
    DURATION: () => {
      throw new FormulaError('#VALUE!');
    },
    // a number stands as seconds already
    SECONDS: () => {
      const [v, ...rest] = argList(criterionArg);
      if (v === undefined || rest.length) throw new FormulaError('#VALUE!');
      return typeof v === 'number' ? v : toSeconds(v);
    },
    COUNTA: () =>
      argList(() => {
        const range = loneRange();
        if (!range) return [expr()].flat().length;
        return range.offsets
          .filter(o => lookup(range.top + o.row, range.left + o.col) !== '')
          .length;
      }).reduce((a, b) => a + b, 0),
  };

  const primary = (): Value => {
    const t = take();
    if (t.kind === 'num') return t.value;
    if (t.kind === 'ref') return ref(t.value);
    if (t.kind === 'fn' && Object.hasOwn(SPECIAL, t.value)) {
      return SPECIAL[t.value]();
    }
    if (t.kind === 'fn') {
      const fn = FUNCTIONS[t.value];
      if (!fn) throw new FormulaError('#NAME?');
      return fn(argList(expr));
    }
    if (t.value === '(') {
      const v = expr();
      take(')');
      return v;
    }
    if (t.value === '-') return -scalar(primary());
    if (t.value === '+') return scalar(primary());
    throw new FormulaError('#PARSE!');
  };

  const term = (): Value => {
    let v = primary();
    while (peek()?.value === '*' || peek()?.value === '/') {
      const op = take().value;
      const rhs = scalar(primary());
      if (op === '/' && rhs === 0) throw new FormulaError('#DIV/0!');
      v = op === '*' ? scalar(v) * rhs : scalar(v) / rhs;
    }
    return v;
  };

  const expr = (): Value => {
    let v = term();
    while (peek()?.value === '+' || peek()?.value === '-') {
      const op = take().value;
      const rhs = scalar(term());
      v = op === '+' ? scalar(v) + rhs : scalar(v) - rhs;
    }
    return v;
  };

  const countBy = (): Grid => {
    take('(');
    const range = rangeArg();
    take(')');
    const groups = new Map<string, [number | string, number]>();
    range.offsets
      .map(o => lookup(range.top + o.row, range.left + o.col))
      .filter(v => v !== '')
      .forEach(v => {
        const key = typeof v === 'number' ? `n${v}` : `s${v.toLowerCase()}`;
        const [first, count] = groups.get(key) ?? [v, 0];
        groups.set(key, [first, count + 1]);
      });
    if (!groups.size) throw new FormulaError('#CALC!');
    return [...groups.values()].map(([v, count]) => [
      typeof v === 'number' ? format(v) : v,
      count,
    ]);
  };

  const duration = (): string => {
    const [seconds, ...rest] = argList(expr);
    if (seconds === undefined || rest.length) throw new FormulaError('#VALUE!');
    return clock(scalar(seconds));
  };

  // formulas whose result is no number, so nothing else can take it
  const WHOLE: Record<string, () => Grid | string> = {
    COUNTBY: countBy,
    DURATION: duration,
  };
  const first = tokens[0];
  const whole = first?.kind === 'fn' && Object.hasOwn(WHOLE, first.value)
    ? (i++, WHOLE[first.value]())
    : scalar(expr());
  if (i < tokens.length) throw new FormulaError('#PARSE!');
  return whole;
};

const asNumber = (raw: string) => {
  const n = Number(raw.replace(/,/g, ''));
  return raw !== '' && Number.isFinite(n) ? n : null;
};

// "5:20" is 320 and "1:05:30" is 3930; past the first, a part is under 60
const toSeconds = (text: string) => {
  const m = text.trim().match(/^(?:(\d+):)?(\d+):(\d+(?:\.\d+)?)$/);
  if (!m) throw new FormulaError('#VALUE!');
  const [, hours, minutes, seconds] = m;
  if (Number(seconds) >= 60 || (hours && Number(minutes) >= 60)) {
    throw new FormulaError('#VALUE!');
  }
  return Number(hours ?? 0) * 3600 + Number(minutes) * 60 + Number(seconds);
};

// 320 is "5:20" and 3930 is "1:05:30", to the nearest second
const clock = (seconds: number) => {
  const total = Math.round(seconds);
  const abs = Math.abs(total);
  const pad = (n: number) => String(n).padStart(2, '0');
  const [h, m, s] = [
    Math.floor(abs / 3600),
    Math.floor(abs % 3600 / 60),
    abs % 60,
  ];
  return (total < 0 ? '-' : '')
    + (h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`);
};

const SPILLS = /^=\s*COUNTBY\s*\(/i;

export const evaluate = (table: string[][]): Cell[][] => {
  const results = new Map<string, Cell>();
  const visiting = new Set<string>();
  const rawAt = (row: number, col: number) => table[row]?.[col] ?? '';

  // Formulas that may fill a block, and the blocks they did fill, by key.
  // Before a block is known, an empty cell its own formula reads counts as
  // outside it, and is noted: if the block then covers one, it read itself.
  const anchors: Pos[] = table.flatMap((cells, row) =>
    cells.flatMap((raw, col) => SPILLS.test(raw) ? [{ row, col }] : [])
  );
  const spills = new Map<string, Pos & { grid: Grid }>();
  const readUnder = new Map<string, Pos[]>();

  const covers = (a: Pos, grid: Grid, p: Pos) =>
    p.row >= a.row && p.col >= a.col
    && grid[p.row - a.row]?.[p.col - a.col] !== undefined;

  // the value a block puts on an empty cell, the first block's in reading
  // order where blocks overlap
  const spilledOnto = (row: number, col: number) => {
    for (const a of anchors) {
      if (a.row > row || a.col > col) continue;
      const key = `${a.row},${a.col}`;
      if (visiting.has(key)) {
        readUnder.set(key, [...(readUnder.get(key) ?? []), { row, col }]);
        continue;
      }
      cellAt(a.row, a.col);
      const spill = spills.get(key);
      if (spill && covers(a, spill.grid, { row, col })) {
        return { value: spill.grid[row - a.row][col - a.col], from: a };
      }
    }
    return undefined;
  };

  // a block's first value is its formula's own; the rest must land on
  // empty cells, and on none the formula read
  const spill = (at: Pos, key: string, raw: string, grid: Grid): Cell => {
    const under = grid.flatMap((cells, r) =>
      cells.map((_, c) => ({ row: at.row + r, col: at.col + c }))
    ).slice(1);
    if (under.some(p => rawAt(p.row, p.col) !== '')) {
      throw new FormulaError('#SPILL!');
    }
    if ((readUnder.get(key) ?? []).some(p => covers(at, grid, p))) {
      throw new FormulaError('#CYCLE!');
    }
    spills.set(key, { ...at, grid });
    return { raw, value: grid[0][0] };
  };

  const cellAt = (row: number, col: number): Cell => {
    const key = `${row},${col}`;
    const known = results.get(key);
    if (known) return known;
    const raw = rawAt(row, col);
    if (!raw.startsWith('=')) {
      const spilled = raw === '' ? spilledOnto(row, col) : undefined;
      const cell = spilled
        ? { raw, value: spilled.value, spilled: spilled.from }
        : { raw, value: asNumber(raw) ?? raw };
      results.set(key, cell);
      return cell;
    }
    if (visiting.has(key)) throw new FormulaError('#CYCLE!');
    visiting.add(key);
    let cell: Cell;
    try {
      const result = parseFormula(raw.slice(1), lookup);
      cell = Array.isArray(result)
        ? spill({ row, col }, key, raw, result)
        : {
          raw,
          value: typeof result === 'number' ? format(result) : result,
        };
    } catch (e) {
      if (!(e instanceof FormulaError)) throw e;
      cell = { raw, value: e.message, error: e.message };
    } finally {
      visiting.delete(key);
    }
    results.set(key, cell);
    return cell;
  };

  // a formula that refers to a broken cell is broken too
  const lookup: Lookup = (row, col) => {
    const cell = cellAt(row, col);
    if (cell.error) throw new FormulaError(cell.error);
    return cell.value;
  };

  // every block is known once each formula has run; the result is square,
  // and as big as the table and every block
  table.forEach((cells, row) => cells.forEach((_, col) => cellAt(row, col)));
  const blocks = [...spills.values()];
  const height = Math.max(
    table.length,
    ...blocks.map(b => b.row + b.grid.length),
  );
  const width = Math.max(
    ...table.map(cells => cells.length),
    ...blocks.map(b => b.col + Math.max(...b.grid.map(r => r.length))),
    0,
  );
  return Array.from(
    { length: height },
    (_, row) => Array.from({ length: width }, (_, col) => cellAt(row, col)),
  );
};

// floating point noise off the end: 0.1 + 0.2 shows as 0.3
const format = (n: number) => Math.round(n * 1e10) / 1e10;

// how a cell reads in the grid: a fraction shows at least two decimals, so
// 2.5 shows as 2.50, and 1/3 keeps all its digits
export const display = (value: Cell['value']): string =>
  typeof value !== 'number' || Number.isInteger(value)
    || String(value).includes('e')
    ? String(value)
    : value.toFixed(Math.max(2, decimals(value)));

const decimals = (n: number) => String(n).split('.')[1]?.length ?? 0;
